import * as React from 'react';
import { StyledComponent } from 'styled-components';
import Typography from '../commonComponents/Typography/Typography';

import {
  SecondaryButton,
  PrimaryButton,
  Impact1Button,
  Impact2Button,
  SuccessButton,
  TextButton,
  DangerButton,
  WarningButton,
  ActiveButton,
  AccentButton,
  StyledButtonProps,
  ButtonType
} from './Button.style';

export interface IButtonProps
  extends Omit<StyledButtonProps, 'color'>,
    React.HTMLAttributes<HTMLButtonElement> {
  /**
   * Disabled state needed as prop for styled-components
   *
   * @default false
   */
  disabled?: boolean;

  /**
   * The `type` HTML attribute of the button.
   */
  type?: 'button' | 'submit' | 'reset';

  /**
   * Button kind
   */
  kind?: ButtonType;

  /**
   * Determines if button should span full width of parent
   */
  fullWidth?: boolean;
}

/**
 * - Returns correct StyledButton based on props.intent
 * - Ex: handleButtonSelection('primary') => <PrimaryButton />
 *  @param intent - {String}
 */
const handleButtonSelection = (
  kind: ButtonType = 'primary'
): StyledComponent<'button', any, StyledButtonProps> => {
  const btnTypes = {
    primary: PrimaryButton,
    secondary: SecondaryButton,
    success: SuccessButton,
    warning: WarningButton,
    danger: DangerButton,
    active: ActiveButton,
    impact1: Impact1Button,
    impact2: Impact2Button,
    accent: AccentButton,
    text: TextButton
  };
  return btnTypes[kind];
};

/**
 * Button component. Can optionally render an icon on either side its
 * text content.
 */
const Button: React.FC<IButtonProps> = ({
  kind = 'primary',
  type = 'button',
  withIconOnLeft,
  withIconOnRight,
  children,
  fontSize,
  fontWeight,
  textTransform,
  ...props
}) =>{
  const StyledButton = handleButtonSelection(kind);

  return (
    <StyledButton {...props}>
      {withIconOnLeft}
      <Typography
        kind="button"
        fontSize={fontSize}
        fontWeight={fontWeight}
        textTransform={textTransform}
      >
        {children}
      </Typography>
      {withIconOnRight}
    </StyledButton>
  );
};

Button.displayName = 'Button';
export default Button;
